import { useQuery } from "@apollo/client";
import { Loader2, User } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import type { CustomerDetailFragment, LoyaltyTier } from "@/gql/graphql";
import { LOYALTY_TIERS_QUERY } from "@/graphql/loyalty";
import { useConfig } from "@/hooks/configuration";
import { useIntersectionObserver } from "@/lib/hooks/use-intersection-observer";
import { useSalonStore } from "@/lib/store/salon";
import { CustomerListItem } from "./cards/customer-list-item";

interface CustomerListProps {
  customers: CustomerDetailFragment[];
  onCustomerSelect: (customerId: string) => void;
  loading: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

export function CustomerList({
  customers,
  onCustomerSelect,
  loading,
  hasMore = false,
  onLoadMore,
}: CustomerListProps) {
  const { t } = useTranslation("customers");
  const { currentSalon } = useSalonStore();
  const { value: loyaltyEnabled = false } = useConfig("loyalty.enabled");
  const { data: loyaltyTiersData } = useQuery(LOYALTY_TIERS_QUERY, {
    variables: { franchiseId: currentSalon?.franchiseId || "" },
    skip: !currentSalon?.franchiseId || !loyaltyEnabled,
  });

  const loyaltyTiers = loyaltyTiersData?.loyaltyTiers || [];

  // Calculate tier stats dynamically
  const tierStats = loyaltyTiers.map((tier: LoyaltyTier) => ({
    tier,
    count: customers.filter((c) => c.currentTierId === tier.id).length,
  }));

  // Infinite scroll intersection observer
  const { targetRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: "100px",
  });

  useEffect(() => {
    if (isIntersecting && hasMore && onLoadMore && !loading) {
      onLoadMore();
    }
  }, [isIntersecting, hasMore, onLoadMore, loading]);

  // Stats card component - only render if loyalty is enabled
  const StatsCard = () => {
    if (!loyaltyEnabled || tierStats.length === 0) {
      return null;
    }

    return (
      <Card>
        <CardContent className="py-4">
          <div className={`grid grid-cols-${Math.min(tierStats.length, 4)} divide-x divide-border`}>
            {tierStats.slice(0, 4).map(({ tier, count }) => (
              <div key={tier.id} className="flex flex-col items-center px-4">
                <div className="text-2xl font-bold" style={{ color: tier.color }}>
                  {count}
                </div>
                <div className="text-sm text-muted-foreground font-medium">{tier.name}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };
  // Remove the loading overlay that hides customers
  // This was breaking infinite scroll during loadMore

  // Empty state (only when not loading)
  if (customers.length === 0 && !loading) {
    return (
      <div className="text-center py-12">
        <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-foreground mb-2">{t("list.empty.title")}</h3>
        <p className="text-gray-600">{t("list.empty.description")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <StatsCard />
      <Card>
        <CardContent className="p-0">
          {customers.map((customer, index) => (
            <CustomerListItem
              key={customer.id}
              customer={customer}
              onClick={() => onCustomerSelect(customer.id)}
              isLast={index === customers.length - 1}
            />
          ))}

          {/* Infinite scroll trigger and loading indicator */}
          {hasMore && (
            <div
              ref={targetRef}
              className="flex items-center justify-center py-4 px-6 border-t border-border"
            >
              {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">{t("list.loadingMore")}</span>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">{t("list.scrollToLoadMore")}</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
